import { useEffect, useRef, useState } from "react";

export default function DeleteOrgModal({
  orgName,
  onConfirm,
  onClose,
}: {
  orgName: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [step, setStep] = useState<"confirm" | "final">("confirm");
  const [deleting, setDeleting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  async function handleDelete() {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.4)]"
      role="dialog"
      aria-modal="true"
      aria-label="Delete organization"
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-[20px] p-5 w-[90%] max-w-md shadow-[1px_1px_0px_0px_#1c1b2e] border border-text flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="text-[#cc0000] text-[1.125rem] font-black">Delete Organization</span>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white cursor-pointer"
          >
            <span className="text-text font-bold">✕</span>
          </button>
        </div>

        {step === "confirm" ? (
          <>
            <p className="text-text text-sm leading-5">
              This will permanently delete <strong>{orgName}</strong> and remove all members from
              the organization. This action cannot be undone.
            </p>

            <div className="bg-[rgba(255,59,48,0.06)] rounded-[12px] p-3 border border-[rgba(255,59,48,0.15)]">
              <span className="text-text text-xs font-bold">
                All members will have their org affiliation removed. Competitions and teams linked
                to this org will remain but will no longer be org-scoped.
              </span>
            </div>

            <button
              type="button"
              onClick={() => setStep("final")}
              className="w-full bg-[#cc0000] rounded-[16px] py-3 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer"
            >
              I understand, continue
            </button>
          </>
        ) : (
          <>
            <p className="text-text text-sm leading-5">
              Type <strong>{orgName}</strong> below to confirm deletion.
            </p>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type "${orgName}" to confirm`}
              className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep("confirm")}
                className="flex-1 bg-card rounded-[16px] py-3 text-text text-[0.875rem] font-extrabold border border-text cursor-pointer"
                disabled={deleting}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={confirmText !== orgName || deleting}
                className="flex-1 bg-[#cc0000] rounded-[16px] py-3 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete permanently"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
